import { FormEvent, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import {
  createInstance,
  createSection,
  getInstance,
  getInstanceSchema,
  getSection,
  getSectionSchema,
  updateInstance,
  updateSection,
  type InstanceRef,
} from "@/api/catalog";
import { ApiError } from "@/api/client";
import { Field, Input, Select } from "@/components/form";
import { GatewayVersionHistory } from "@/components/GatewayVersionHistory";
import { JsonPayloadEditor } from "@/components/JsonPayloadEditor";
import { MutationError, RefreshFailed, isLoadingOrPaused, loadFailure } from "@/components/Spinner";
import type {
  GatewayCatalogKind,
  GatewayInstanceKind,
} from "@/types/api";
import { FormActionBar, FormPlaceholder, Notice, Page, PageHeader } from "@/components/ui";
import { useDirtyValues } from "@/hooks/useDirtyValues";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";

// "customer" is deliberately absent: customer types are composed from
// catalog components on their own page (CatalogTypeEdit.tsx) — there's no
// raw-JSON path left for them, by design. This editor now only handles
// cloud instances and global sections.
const KINDS: { value: GatewayCatalogKind; label: string }[] = [
  { value: "cloud", label: "Cloud instance" },
  { value: "section", label: "Section (global)" },
];

// Mirror of backend regexes (lambda-layers/.../models.py: _GATEWAY_MODEL_RE /
// _GATEWAY_VERSION_RE). Browser-level rejection gives the operator an
// immediate hint instead of a server round-trip — the server stays
// authoritative on the actual validation.
const MODEL_PATTERN = "[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?";
const VERSION_PATTERN = "v\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}";

/**
 * Parse the ``?cloneFrom=<kind>/<model>/<version>`` query param used to
 * pre-fill the editor when an operator clicks "Clone as new version" on
 * an existing cloud instance. Customer types have no raw payload to clone
 * from anymore, so "customer" is refused here too.
 */
function parseCloneFrom(raw: string | null): InstanceRef | null {
  if (!raw) return null;
  const parts = raw.split("/");
  if (parts.length !== 3) return null;
  const [kind, model, version] = parts;
  if (kind !== "cloud") return null;
  if (!model || !version) return null;
  return { kind: kind as GatewayInstanceKind, model, version };
}

export function CatalogGatewayEditPage() {
  // Two route shapes feed this page:
  //   /catalog/gateways/instances/:kind/:model/:version/edit  (cloud | customer)
  //   /catalog/gateways/sections/:name/edit
  // The "new" route has no params, so all four are undefined.
  const params = useParams<{
    kind?: string;
    model?: string;
    version?: string;
    name?: string;
  }>();
  const [searchParams] = useSearchParams();

  const isEdit = Boolean(
    (params.kind && params.model && params.version) || params.name,
  );
  const routeKind: GatewayCatalogKind = params.name
    ? "section"
    : (params.kind as GatewayCatalogKind | undefined) ?? "cloud";

  // We memoize on the raw query-string value rather than the
  // ``searchParams`` object: react-router hands back a fresh object
  // on every render even when the URL is unchanged, which would otherwise
  // produce a fresh ``cloneFrom`` identity each render and ripple into
  // ``useEffect`` / ``useQuery`` deps below.
  const rawCloneFrom = searchParams.get("cloneFrom");
  const cloneFrom = useMemo(
    () => (!isEdit ? parseCloneFrom(rawCloneFrom) : null),
    [isEdit, rawCloneFrom],
  );

  const auth = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const token = auth.user?.id_token ?? "";

  // Stable identity tuple — used as the React Query key and as the effect
  // dependency that resets the prime flag when the operator navigates
  // between two edit routes without unmounting the component.
  const routeName = params.name ?? null;
  const routeModel = params.model ?? null;
  const routeVersion = params.version ?? null;
  const queryKey = [
    "catalog",
    "gateway",
    routeKind,
    routeModel ?? routeName,
    routeVersion,
  ] as const;

  const existing = useQuery({
    queryKey,
    queryFn: () => {
      if (routeKind === "section") {
        return getSection(token, routeName!);
      }
      return getInstance(token, {
        kind: routeKind as GatewayInstanceKind,
        model: routeModel!,
        version: routeVersion!,
      });
    },
    // Customer types redirect away below without needing this fetch — see
    // the guard right before the render return.
    enabled: !!token && isEdit && routeKind !== "customer",
  });

  // Source fetch for "Clone as new version" — pulls the payload of the
  // instance referenced in ?cloneFrom= so we can prime the form.
  const cloneSource = useQuery({
    queryKey: ["catalog", "gateway-clone-source", cloneFrom],
    queryFn: () => getInstance(token, cloneFrom!),
    enabled: !!token && !!cloneFrom,
  });

  const [kind, setKind] = useState<GatewayCatalogKind>(
    cloneFrom?.kind ?? routeKind,
  );
  const [model, setModel] = useState<string>(
    cloneFrom?.model ?? routeModel ?? routeName ?? "",
  );
  const [version, setVersion] = useState<string>(routeVersion ?? "");
  const [payloadText, setPayloadText] = useState<string>("{\n  \n}\n");
  const [primed, setPrimed] = useState(false);
  const [jsonValid, setJsonValid] = useState(true);
  // A form that creates an entry from nothing is open from the first render; one that edits an entry,
  // or clones one, is filled in when its source arrives (``primed``). Dirty = what the form shows
  // differs from what it was opened with, and the way out of the page is held while it is.
  const { dirty, reset: markSaved } = useDirtyValues(
    { kind, model, version, payloadText },
    isEdit || cloneFrom ? primed : true,
  );
  const guard = useUnsavedChanges(dirty);

  // React Router keeps this component mounted when only the URL params
  // change (both edit routes map to the same element), so we must reset
  // the prime flag explicitly when the route identity changes — otherwise
  // a navigation from instance edit → section edit keeps stale form state.
  useEffect(() => {
    setPrimed(false);
  }, [routeKind, routeModel, routeVersion, routeName, cloneFrom?.kind, cloneFrom?.model, cloneFrom?.version]);

  // Prime the form from the edited entry (edit mode) or from the clone
  // source (new entry with ?cloneFrom=...). The two branches are mutually
  // exclusive — cloneFrom is only parsed when ``!isEdit``.
  useEffect(() => {
    if (primed) return;
    if (isEdit && existing.data) {
      setKind(existing.data.kind);
      setModel(existing.data.model);
      setVersion(existing.data.version ?? "");
      setPayloadText(JSON.stringify(existing.data.payload, null, 2));
      setPrimed(true);
      return;
    }
    if (!isEdit && cloneFrom && cloneSource.data) {
      // Clone copies the payload + kind + model from the source, but
      // leaves the version field empty so the operator makes an
      // explicit semantic decision (bump major/minor/patch).
      setKind(cloneSource.data.kind);
      setModel(cloneSource.data.model);
      setVersion("");
      setPayloadText(JSON.stringify(cloneSource.data.payload, null, 2));
      setPrimed(true);
    }
  }, [existing.data, isEdit, primed, cloneFrom, cloneSource.data]);

  // Indicative JSON schema for Monaco — drives autocomplete and hover
  // docs. A missing schema (404) is *not* an error: the editor falls
  // back to plain JSON syntax checking.
  const schemaQuery = useQuery({
    queryKey: ["catalog", "gateway-schema", kind, model],
    queryFn: () => {
      if (kind === "section") {
        return getSectionSchema(token, model);
      }
      return getInstanceSchema(token, kind);
    },
    enabled: !!token && (kind === "section" ? !!model : true),
  });

  const mut = useMutation({
    mutationFn: () => {
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(payloadText);
      } catch (err) {
        throw new Error(
          `payload is not valid JSON: ${(err as Error).message}`,
        );
      }
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        throw new Error("payload must be a JSON object (not an array or scalar)");
      }

      if (kind === "section") {
        return isEdit
          ? updateSection(token, model, parsed)
          : createSection(token, model, parsed);
      }
      if (!version) {
        throw new Error("Version is required for cloud/customer instances");
      }
      const ref: InstanceRef = {
        kind: kind as GatewayInstanceKind,
        model,
        version,
      };
      return isEdit ? updateInstance(token, ref, parsed) : createInstance(token, ref, parsed);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "gateways"] });
      // In edit mode the route params drive the cache entry, so re-use
      // them here. In create mode the queryKey was disabled, so this is a
      // no-op invalidation — harmless and keeps the logic symmetric.
      qc.invalidateQueries({ queryKey });
      markSaved();
      guard.leave("/catalog/gateways");
    },
  });

  // A stale link or a hand-typed URL can still land here for a customer
  // type (e.g. a bookmark from before this route split). Redirect to the
  // composer rather than rendering a raw-JSON form for a payload that no
  // longer exists — the identity is known from the URL params alone, no
  // need to wait for the fetch.
  if (isEdit && routeKind === "customer" && routeModel && routeVersion) {
    return (
      <Navigate
        to={`/catalog/gateways/types/${encodeURIComponent(routeModel)}/${encodeURIComponent(routeVersion)}/edit`}
        replace
      />
    );
  }

  // The title comes from the address, so loading and failing both keep it.
  const title = isEdit
    ? kind === "section"
      ? `Edit section/${model}`
      : `Edit ${kind}/${model}@${version}`
    : cloneFrom
      ? `Clone ${cloneFrom.kind}/${cloneFrom.model}@${cloneFrom.version} → new version`
      : "New catalog entry";
  const header = <PageHeader back={{ to: "/catalog/gateways", label: "Gateway types" }} title={title} />;

  if ((isEdit && isLoadingOrPaused(existing)) || (cloneFrom && isLoadingOrPaused(cloneSource))) {
    return <FormPlaceholder header={header} width="list" />;
  }
  // Only an error with nothing to show replaces the page (see RefreshFailed):
  // a failed refetch must not hide the form the operator is filling in.
  const failure = (isEdit ? loadFailure(existing) : undefined) ?? (cloneFrom ? loadFailure(cloneSource) : undefined);
  if (failure) return <FormPlaceholder header={header} width="list" failure={failure} />;

  const schema = schemaQuery.data?.schema as
    | Record<string, unknown>
    | undefined;
  const showHistory = isEdit && kind !== "section" && !!model;

  const form = (
    <form
      onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        mut.mutate();
      }}
      className="max-w-form flex-1"
    >
      <div className="card space-y-5">
        <Field
          label="Kind"
          help={
            isEdit
              ? "Kind cannot be changed — it's part of the entry's identity."
              : undefined
          }
        >
          <Select
            value={kind}
            disabled={isEdit}
            onChange={(e) => setKind(e.target.value as GatewayCatalogKind)}
            options={KINDS}
          />
        </Field>

        <Field
          label={kind === "section" ? "Name" : "Model"}
          help={
            isEdit
              ? `${kind === "section" ? "Name" : "Model"} cannot be changed — it's part of the entry's identity.`
              : undefined
          }
        >
          <Input
            mono
            value={model}
            required
            disabled={isEdit}
            pattern={MODEL_PATTERN}
            title="DNS-safe slug: starts and ends with [a-z0-9], hyphens allowed in the middle (64 characters max)."
            onChange={(e) => setModel(e.target.value)}
          />
        </Field>

        {kind !== "section" && (
          <Field
            label="Version"
            help={
              isEdit
                ? "Version cannot be changed — each version is immutable."
                : cloneFrom
                  ? `Bump from ${cloneFrom.version} — the target must be new.`
                  : "Semver vX.Y.Z — bump to publish a revision."
            }
          >
            <Input
              mono
              value={version}
              required
              disabled={isEdit}
              placeholder="v1.0.0"
              pattern={VERSION_PATTERN}
              title="Semver vX.Y.Z (1 to 3 digits per segment, e.g. v1.0.0)"
              onChange={(e) => setVersion(e.target.value)}
            />
          </Field>
        )}

        <div>
          <div className="mb-1 flex items-baseline justify-between">
            <span className="text-sm font-medium text-ink">Payload (JSON)</span>
            {schema && (
              <span className="text-xs text-ink-muted">
                Indicative schema loaded — autocomplete enabled.
              </span>
            )}
          </div>
          <JsonPayloadEditor
            ariaLabel="Payload (JSON)"
            value={payloadText}
            onChange={setPayloadText}
            schema={schema}
            onValidity={setJsonValid}
          />
          <p className="mt-1 text-xs text-ink-muted">
            The schema is indicative (input assistance); final validation
            is applied at deploy time.
          </p>
        </div>
      </div>

      <FormActionBar
        dirty={dirty}
        submitLabel={isEdit ? "Save" : "Create entry"}
        pending={mut.isPending}
        disabled={!jsonValid}
        disabledReason="Invalid JSON — fix before saving"
        onCancel={() => navigate("/catalog/gateways")}
        error={
          <MutationError
            mutation={mut}
            title="Could not save the entry"
            // 409 on create/clone means the (kind, model, version) triple already exists in the
            // catalog — most often because the operator forgot to bump the version when cloning.
            // A specific message instead of the raw backend error.
            view={(error) =>
              !isEdit && error instanceof ApiError && error.status === 409 ? (
                <Notice tone="warning" role="alert">
                  This version already exists. Enter a new version (major, minor, or patch bump) before saving.
                </Notice>
              ) : undefined
            }
          />
        }
      />
    </form>
  );

  return (
    <Page>
      {header}

      <RefreshFailed query={existing} className="mb-6" />
      <RefreshFailed query={cloneSource} className="mb-6" />

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {form}
        {showHistory && (
          <GatewayVersionHistory
            kind={kind as GatewayInstanceKind}
            model={model}
            currentVersion={version}
          />
        )}
      </div>
    </Page>
  );
}
